import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { formatError } from '../api/client';
import { Plus, Search, Edit, Trash2, Eye, X, ChevronLeft, ChevronRight, Users, Filter } from 'lucide-react';

const VILLES = ['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès', 'Oujda', 'Kenitra', 'Tétouan', 'Safi', 'El Jadida', 'Béni Mellal', 'Nador', 'Laâyoune', 'Autre'];
const GROUPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const LIMIT = 50;

const EMPTY_FORM = {
  nom: '', prenom: '', date_naissance: '', sexe: 'M',
  CIN: '', CNSS_AMO: '', telephone: '', email: '', adresse: '', ville: '',
  groupe_sanguin: '', allergies: '', antecedents_personnels: '',
  antecedents_familiaux: '', medicaments_actuels: '',
  profession: '', situation_familiale: '', couverture_medicale: ''
};

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [villeFilter, setVilleFilter] = useState('');
  const [sexeFilter, setSexeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async (p = 1, q = '', ville = '', sexe = '') => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT };
      if (q) params.search = q;
      if (ville) params.ville = ville;
      if (sexe) params.sexe = sexe;
      const res = await API.get('/patients', { params });
      setPatients(res.data.data);
      setTotal(res.data.total);
      setPage(res.data.page);
      setPages(res.data.pages);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, search, villeFilter, sexeFilter); }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(1, search, villeFilter, sexeFilter); }, 350);
    return () => clearTimeout(t);
  }, [search, villeFilter, sexeFilter]);

  const goToPage = (p) => {
    if (p < 1 || p > pages) return;
    load(p, search, villeFilter, sexeFilter);
    window.scrollTo(0, 0);
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditPatient(null); setShowModal(true); setError(''); };
  const openEdit = (p) => { setForm({ ...EMPTY_FORM, ...p }); setEditPatient(p); setShowModal(true); setError(''); };

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      if (editPatient) { await API.put(`/patients/${editPatient.id}`, form); }
      else { await API.post('/patients', form); }
      setShowModal(false);
      load(editPatient ? page : 1, search, villeFilter, sexeFilter);
    } catch (e) { setError(formatError(e)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce patient ?')) return;
    await API.delete(`/patients/${id}`);
    load(page, search, villeFilter, sexeFilter);
  };

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );
  const inp = (field, type = 'text', placeholder = '') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );

  // Pagination helpers
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    for (let i = Math.max(1, page - delta); i <= Math.min(pages, page + delta); i++) range.push(i);
    const result = [];
    if (range[0] > 1) { result.push(1); if (range[0] > 2) result.push('…'); }
    result.push(...range);
    if (range[range.length - 1] < pages) { if (range[range.length - 1] < pages - 1) result.push('…'); result.push(pages); }
    return result;
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="patients-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Patients</h1>
          <p className="text-stone-500 text-sm mt-0.5">
            {loading ? '...' : `${total.toLocaleString('fr-FR')} patients au total`}
          </p>
        </div>
        <button
          data-testid="add-patient-button"
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={17} /> Nouveau Patient
        </button>
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="patient-search-input"
            type="text"
            placeholder="Rechercher par nom, prénom, CIN, téléphone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={villeFilter}
          onChange={e => setVilleFilter(e.target.value)}
          className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500 min-w-[140px]"
        >
          <option value="">Toutes les villes</option>
          {VILLES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select
          value={sexeFilter}
          onChange={e => setSexeFilter(e.target.value)}
          className="px-3 py-2.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500"
        >
          <option value="">Tous</option>
          <option value="M">Hommes</option>
          <option value="F">Femmes</option>
        </select>
        {(villeFilter || sexeFilter) && (
          <button onClick={() => { setVilleFilter(''); setSexeFilter(''); }} className="px-3 py-2.5 text-stone-500 hover:text-stone-700 text-xs border border-stone-300 rounded-lg hover:bg-stone-50">
            Effacer filtres
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <table className="w-full" data-testid="patients-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {['Patient', 'Date naissance', 'CIN', 'Téléphone', 'Ville', 'Couverture', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {patients.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-14 text-stone-400">
                  <Users size={40} className="text-stone-200 mx-auto mb-3" />
                  <p className="text-sm">{search ? `Aucun résultat pour "${search}"` : 'Aucun patient'}</p>
                </td></tr>
              ) : patients.map(p => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/patients/${p.id}`)}
                  data-testid="patient-table-row"
                  className="hover:bg-stone-50/60 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-700 font-semibold text-xs">{p.prenom?.[0]}{p.nom?.[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-stone-800">{p.prenom} {p.nom}</p>
                        <p className="text-xs text-stone-400">{p.sexe === 'M' ? 'Homme' : 'Femme'}{p.groupe_sanguin ? ` · ${p.groupe_sanguin}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">{p.date_naissance || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600 font-mono">{p.CIN || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{p.telephone || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{p.ville || '—'}</td>
                  <td className="px-4 py-3">
                    {p.couverture_medicale && (
                      <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{p.couverture_medicale}</span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/patients/${p.id}`)} className="p-1.5 text-stone-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Voir fiche">
                        <Eye size={15} />
                      </button>
                      <button onClick={() => openEdit(p)} className="p-1.5 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Modifier">
                        <Edit size={15} />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Supprimer">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="pagination">
          <p className="text-sm text-stone-500">
            Page <b>{page}</b> sur <b>{pages}</b> · {total.toLocaleString('fr-FR')} patients
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
              className="p-2 border border-stone-300 rounded-lg text-stone-500 hover:bg-stone-50 disabled:opacity-40 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            {getPageNumbers().map((n, i) => (
              n === '…'
                ? <span key={`e${i}`} className="px-2 text-stone-400">…</span>
                : <button
                    key={n}
                    onClick={() => goToPage(n)}
                    className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors border ${
                      n === page
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {n}
                  </button>
            ))}
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page === pages}
              className="p-2 border border-stone-300 rounded-lg text-stone-500 hover:bg-stone-50 disabled:opacity-40 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-modal">
            <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-white z-10">
              <h2 className="font-heading font-semibold text-stone-800">{editPatient ? 'Modifier le Patient' : 'Nouveau Patient'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <F label="Prénom *">{inp('prenom', 'text', 'Prénom')}</F>
                <F label="Nom *">{inp('nom', 'text', 'Nom de famille')}</F>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="Date de naissance">{inp('date_naissance', 'date')}</F>
                <F label="Sexe">
                  <select value={form.sexe} onChange={e => setForm(f => ({ ...f, sexe: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </select>
                </F>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="CIN">{inp('CIN', 'text', 'AB123456')}</F>
                <F label="CNSS/AMO">{inp('CNSS_AMO', 'text', 'Numéro CNSS ou AMO')}</F>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="Téléphone">{inp('telephone', 'tel', '06XXXXXXXX')}</F>
                <F label="Email">{inp('email', 'email', 'email@exemple.com')}</F>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="Ville">
                  <select value={form.ville} onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="">Sélectionner une ville</option>
                    {VILLES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </F>
                <F label="Groupe sanguin">
                  <select value={form.groupe_sanguin} onChange={e => setForm(f => ({ ...f, groupe_sanguin: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="">NC</option>
                    {GROUPES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </F>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <F label="Couverture médicale">
                  <select value={form.couverture_medicale} onChange={e => setForm(f => ({ ...f, couverture_medicale: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="">Aucune</option>
                    <option value="AMO">AMO</option>
                    <option value="CNSS">CNSS</option>
                    <option value="CNOPS">CNOPS</option>
                    <option value="RAMED">RAMED</option>
                    <option value="Mutuelle">Mutuelle</option>
                    <option value="Assurance privée">Assurance privée</option>
                  </select>
                </F>
                <F label="Profession">{inp('profession', 'text')}</F>
              </div>
              <F label="Allergies">
                <textarea rows={2} value={form.allergies || ''} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))}
                  placeholder="Pénicilline, Aspirine..."
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
              <F label="Antécédents personnels">
                <textarea rows={3} value={form.antecedents_personnels || ''} onChange={e => setForm(f => ({ ...f, antecedents_personnels: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
              <F label="Antécédents familiaux">
                <textarea rows={2} value={form.antecedents_familiaux || ''} onChange={e => setForm(f => ({ ...f, antecedents_familiaux: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
              <F label="Médicaments actuels">
                <textarea rows={2} value={form.medicaments_actuels || ''} onChange={e => setForm(f => ({ ...f, medicaments_actuels: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowModal(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button
                data-testid="save-patient-button"
                onClick={handleSave}
                disabled={saving || !form.nom || !form.prenom}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
